<template>
    <div class="sms-geo">
        <div class="sms-geo__map">
            <GMapMap
                ref="mapRef"
                :center="viewCenter"
                :zoom="viewZoom"
                style="width: 100%; height: 280px"
                :options="{ fullscreenControl: false, streetViewControl: false, mapTypeControl: false }"
                @tilesloaded="onTilesLoaded"
                @click="onMapClick"
            >
                <GMapMarker
                    v-for="(point, index) in markers"
                    :key="`marker-${index}`"
                    :position="point"
                    :label="String(index + 1)"
                    :draggable="!disabled"
                    @dragend="onDragEnd(index, $event)"
                />
                <GMapCircle
                    v-for="(point, index) in circles"
                    :key="`circle-${index}`"
                    :center="point"
                    :radius="point.radius"
                    :options="{ fillColor: '#2563eb', fillOpacity: 0.15, strokeColor: '#2563eb', strokeWeight: 1 }"
                />
            </GMapMap>
        </div>
        <p class="section-hint">Город задаёт регион. Точка с радиусом сужает рассылку внутри выбранных городов. Без точки — весь город.</p>
        <ul v-if="points.length" class="sms-geo__list">
            <li v-for="(point, index) in points" :key="`point-${index}`" class="sms-geo__point">
                <span class="sms-geo__coords">{{ index + 1 }}. {{ formatCoordinate(point.lat) }}, {{ formatCoordinate(point.lng) }}</span>
                <label class="sms-geo__radius">
                    <span>Радиус, м</span>
                    <input
                        :value="point.radius_m ?? ''"
                        type="number"
                        min="100"
                        max="50000"
                        step="1"
                        class="form-input"
                        :disabled="disabled"
                        @input="onRadiusInput(index, $event)"
                    />
                </label>
                <button type="button" class="btn btn--secondary btn--small" :disabled="disabled" @click="remove(index)">
                    Удалить
                </button>
            </li>
        </ul>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { Circle, Map, Marker } from '@fawmi/vue-google-maps';

const GMapMap = Map;
const GMapMarker = Marker;
const GMapCircle = Circle;

const MAX_POINTS = 20;
const DEFAULT_RADIUS_M = 1000;
const KAZAKHSTAN = { lat: 48.11, lng: 66.22 };
const COUNTRY_ZOOM = 5;
const CITY_ZOOM = 11;
const POINT_ZOOM = 12;

const props = defineProps({
    points: { type: Array, default: () => [] },
    places: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update']);

const markers = computed(() => props.points
    .filter(point => Number.isFinite(Number(point?.lat)) && Number.isFinite(Number(point?.lng)))
    .map(point => ({ lat: Number(point.lat), lng: Number(point.lng) })));

const circles = computed(() => props.points.flatMap((point) => {
    const lat = Number(point?.lat);
    const lng = Number(point?.lng);
    const radius = Number(point?.radius_m);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(radius) || radius <= 0) return [];
    return [{ lat, lng, radius }];
}));

const mapRef = ref(null);
const mapReady = ref(false);
const viewCenter = ref({ ...KAZAKHSTAN });
const viewZoom = ref(COUNTRY_ZOOM);

const cityPlaces = computed(() => (props.places || [])
    .map((place) => ({ lat: Number(place?.lat), lng: Number(place?.lng) }))
    .filter((place) => Number.isFinite(place.lat) && Number.isFinite(place.lng)));

const frameTargets = () => [...cityPlaces.value, ...markers.value];

const frameKey = computed(() => `${cityPlaces.value.map((place) => `${place.lat},${place.lng}`).join('|')}#${markers.value.length}`);

const applyFrame = () => {
    const component = mapRef.value;
    if (!mapReady.value || !component?.panTo || !component?.fitBounds) return;
    const targets = frameTargets();
    if (targets.length <= 1) {
        const center = targets[0] || KAZAKHSTAN;
        viewCenter.value = { lat: center.lat, lng: center.lng };
        viewZoom.value = targets.length === 0 ? COUNTRY_ZOOM : (cityPlaces.value.length ? CITY_ZOOM : POINT_ZOOM);
        component.panTo(viewCenter.value);
        return;
    }

    const bounds = new google.maps.LatLngBounds();
    targets.forEach((point) => bounds.extend(point));
    component.fitBounds(bounds, 48);
};

const onTilesLoaded = () => {
    if (mapReady.value) return;
    mapReady.value = true;
    applyFrame();
};

watch(frameKey, () => {
    applyFrame();
});

const readLatLng = (event) => {
    const latLng = event?.latLng;
    const lat = typeof latLng?.lat === 'function' ? latLng.lat() : latLng?.lat;
    const lng = typeof latLng?.lng === 'function' ? latLng.lng() : latLng?.lng;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
};

const clonePoints = () => props.points.map(point => ({
    lat: Number(point.lat),
    lng: Number(point.lng),
    radius_m: point.radius_m == null || point.radius_m === '' ? null : Number(point.radius_m),
}));

const onMapClick = (event) => {
    if (props.disabled || props.points.length >= MAX_POINTS) return;
    const next = readLatLng(event);
    if (!next) return;
    const alreadyThere = props.points.some(point => (
        Math.abs(Number(point.lat) - next.lat) < 0.00002 && Math.abs(Number(point.lng) - next.lng) < 0.00002
    ));
    if (alreadyThere) return;
    emit('update', [...clonePoints(), { lat: next.lat, lng: next.lng, radius_m: DEFAULT_RADIUS_M }]);
};

const onDragEnd = (index, event) => {
    if (props.disabled) return;
    const next = readLatLng(event);
    if (!next || !props.points[index]) return;
    const points = clonePoints();
    points[index].lat = next.lat;
    points[index].lng = next.lng;
    emit('update', points);
};

const onRadiusInput = (index, event) => {
    if (!props.points[index]) return;
    const raw = event.target.value;
    const points = clonePoints();
    points[index].radius_m = raw === '' ? null : Number(raw);
    emit('update', points);
};

const remove = (index) => {
    emit('update', clonePoints().filter((_, pointIndex) => pointIndex !== index));
};

const formatCoordinate = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number.toFixed(6).replace(/\.?0+$/, '') : '';
};
</script>

<style scoped lang="scss">
.sms-geo__map {
    width: 100%;
    height: 280px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;

    :deep(.vue-map-container) {
        width: 100%;
        height: 280px;
    }
}

.sms-geo__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 8px 0 0;
    padding: 0;
    list-style: none;
}

.sms-geo__point {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
}

.sms-geo__coords {
    min-width: 180px;
}

.sms-geo__radius {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin: 0;

    .form-input {
        width: 110px;
    }
}
</style>
